import { ConsoleLogger } from '@proposify/frontend-observability';
import type { Editor } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';

const MENU_HEIGHT = 150;

export interface MenuPosition {
  top: number;
  left: number;
}

const getSelectionCoordinates = (editor: Editor, from: number, to: number) => {
  const { view, state } = editor;
  try {
    if (state.selection instanceof NodeSelection) {
      return view.coordsAtPos(state.selection.from);
    }

    const coords = view.coordsAtPos(from);
    const endCoords = view.coordsAtPos(to);

    if (endCoords) {
      return {
        top: coords.top,
        left: coords.left,
        right: endCoords.right,
        bottom: endCoords.bottom,
      };
    }

    return coords;
  } catch (error) {
    ConsoleLogger?.warn?.('Failed to get selection coordinates', error);
    return null;
  }
};

export const calculateMenuPosition = (
  threadPanelRef: React.RefObject<HTMLElement>,
  coords: { top: number; bottom: number }
): MenuPosition | null => {
  const container = threadPanelRef.current || document.body;
  const containerRect = container.getBoundingClientRect();

  const centerY = (coords.top + coords.bottom) / 2;

  return {
    top: centerY - containerRect.top - MENU_HEIGHT / 2,
    left: 0,
  };
};

export const updateMenuPosition = (
  activeEditor: Editor,
  commentsEnabled: boolean,
  setShowMenu: (show: boolean) => void,
  threadPanelRef: React.RefObject<HTMLElement>,
  setMenuPosition: (position: MenuPosition) => void
) => {
  if (!activeEditor || !commentsEnabled) {
    setShowMenu(false);
    return;
  }

  const { state } = activeEditor;
  const { from, to } = state.selection;

  if (from === to) {
    setShowMenu(false);
    return;
  }

  const coords = getSelectionCoordinates(activeEditor, from, to);
  if (!coords) {
    setShowMenu(false);
    return;
  }

  const position = calculateMenuPosition(threadPanelRef, coords);
  if (position) {
    setMenuPosition(position);
    setShowMenu(true);
  } else {
    setShowMenu(false);
  }
};
